import { useQuery } from '@tanstack/react-query';
import type { Aggregation, OptimisationDetail, OptimisationResults } from './types';

export function useOptimisationIds() {
    return useQuery({ queryKey: ['ref-optimisation-detail', 'ids'], queryFn: async () => (await (await fetch('/api/ref-optimisation-detail/ids')).json()) as string[] });
}

export function useOptimisationDetail(eid: string) {
    return useQuery({
        queryKey: ['ref-optimisation-detail', eid],
        queryFn: async () => (await (await fetch(`/api/ref-optimisation-detail/${eid}`)).json()) as OptimisationDetail,
    });
}

const bucketKey = (ts: string, agg: Aggregation) => {
    if (agg === 'hour') return ts;
    if (agg === 'day') return ts.slice(0, 10);
    if (agg === 'month') return ts.slice(0, 7);
    const d = new Date(ts.slice(0, 10));
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return `Semaine du ${monday.toISOString().slice(0, 10)}`;
};

/** Sums hourly results into the requested aggregation (energy in MWh, costs in €). */
export function aggregateResults(results: OptimisationResults, agg: Aggregation) {
    const keys: string[] = [];
    const index = results.timestamps.map((ts) => {
        const k = bucketKey(ts, agg);
        if (!keys.includes(k)) keys.push(k);
        return keys.indexOf(k);
    });
    const sum = (values: number[]) => {
        const out = keys.map(() => 0);
        values.forEach((v, i) => (out[index[i]] += v));
        return out.map((v) => Math.round(v * 1000) / 1000);
    };
    return {
        labels: keys,
        availableProduction: sum(results.availableProduction),
        delivered: sum(results.delivered),
        thermalLosses: sum(results.thermalLosses),
        notDelivered: sum(results.notDelivered),
        productionBySite: Object.fromEntries(Object.entries(results.productionBySite).map(([site, v]) => [site, sum(v)])),
        costs: { fuel: sum(results.costs.fuel), auxiliaryElectricity: sum(results.costs.auxiliaryElectricity), pollutants: sum(results.costs.pollutants) },
    };
}
